import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Redirect } from "react-router-dom";
import GenerativeReportTable from "../../components/analytics/GenerativeReportTable";
import { buildTimezoneAwareDateFilter } from "../../utils/engagementAnalyticsTransformers";
import SessionAnalyticsContainer, { SessionListModel } from "../../hooks/SessionAnalyticsContainer";
import AnalyticsRequestFilter from "../../models/analytics/AnalyticsRequestFilter";
import SessionReview from "../../models/analytics/SessionReview";
import UserContainer from "../../state/containers/UserContainer";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_colors_decline,
    color_defaults_white,
    color_shades_light,
    color_shades_dark,
    color_shades_lightest,
    color_variants_ocean_light_1
} from "../../constants/colors";
import Loader from "../../components/general/Loader";
import useCSVHandler from "../../hooks/UseCSVHandler";
import styled from "@emotion/styled";
import OrganizationContainer from "../../state/containers/OrganizationContainer";
import DropDownSelectField from "../../components/forms/DropDownSelectField";
import ApplicationContainer from "../../state/containers/ApplicationContainer";
import { getBaseUrl } from "../../constants/Urls";
import * as voicifyApi from "../../api";
import ApplicationModel from "../../models/applications/api/ApplicationModel";

const exportIcon = require("../../content/images/menu-item-icons/file-code-download.svg");

const STORAGE_KEY = 'VOICIFY_CROSS_ORG_REVIEW';
const CURRENT_ORG_STORAGE_KEY = 'VOICIFY_CURRENT_ORG_REVIEW';
const DATE_FILTER_STORAGE_KEY = 'VOICIFY_CROSS_ORG_REVIEW_DATES';

interface OrgEntry {
    id: string;
    name: string;
}

interface DateFilter {
    startDate: string;
    endDate: string;
}

const getDefaultDates = (): DateFilter => {
    const now = new Date();
    const startDate = new Date(now.getTime() - 24 * 14 * 60 * 60 * 1000); // 14 days hours ago
    const endDate = new Date(now.getTime() + 8 * 60 * 60 * 1000); // 8 hours from now

    return {
        startDate: startDate.toISOString().slice(0, 16), // Format for datetime-local input
        endDate: endDate.toISOString().slice(0, 16)
    };
};

const getStoredDateFilter = (): DateFilter => {
    const stored = localStorage.getItem(DATE_FILTER_STORAGE_KEY);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            return parsed;
        } catch (error) {
            console.error('Error parsing stored date filter:', error);
        }
    }
    return getDefaultDates();
};

interface CrossOrgSessionReviewProps {
    userContainer: UserContainer;
    organizationContainer: OrganizationContainer;
    appContainer: ApplicationContainer;
    currentOrgMode?: boolean;
}

const CrossOrgSessionReview: React.FC<CrossOrgSessionReviewProps> = ({ userContainer, organizationContainer, appContainer, currentOrgMode }) => {
    // Check user access first, before any hooks
    const currentUser = userContainer.state.currentUser;
    if (currentUser && currentUser.email) {
        const emailLower = currentUser.email.toLowerCase();
        if (!emailLower.endsWith('@voicify.com')) {
            return <Redirect to="/v" />;
        }
    }

    const [orgEntries, setOrgEntries] = useState<OrgEntry[]>([]);
    const [showOrgManager, setShowOrgManager] = useState(false);
    const [dateFilter, setDateFilter] = useState<DateFilter>(getStoredDateFilter());
    const [localStartDate, setLocalStartDate] = useState<string>(getStoredDateFilter().startDate);
    const [localEndDate, setLocalEndDate] = useState<string>(getStoredDateFilter().endDate);
    const [debouncedOrgEntries, setDebouncedOrgEntries] = useState<OrgEntry[]>([]);
    const { exportCSV } = useCSVHandler();
    const [isExporting, setIsExporting] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const [filteredSessionReviews, setFilteredSessionReviews] = useState<SessionReview[]>([]);
    const [selectedFilterTags, setSelectedFilterTags] = useState<string[]>([]);
    const [excludedFilterTags, setExcludedFilterTags] = useState<string[]>([]);
    const [currentOrgApplications, setCurrentOrgApplications] = useState<ApplicationModel[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const sessionContainer = SessionAnalyticsContainer.useContainer();

    useEffect(() => {
        organizationContainer.getOrganizations();
        if (currentOrgMode && organizationContainer.state.currentOrganization?.id) {
            const loadApplications = async () => {
                const result = await voicifyApi.getApplicationsForOrg(organizationContainer.state.currentOrganization.id);
                if (result.resultType === "Ok") {
                    setCurrentOrgApplications(result.data);
                }
            };
            loadApplications();
        }
    }, [currentOrgMode, organizationContainer.state.currentOrganization?.id])

    const columnHeaders = [
        "App Name",
        "Session Start Date",
        "Session Summary",
        "Session Tags",
        "Session Link",
    ];

    const getReportRows = async () => {
        const sessions: SessionListModel[] = sessionContainer.sessions || [];

        let dataToExport: any[] = [];
        if (filteredSessionReviews.length > 0) {
            const sessionMap = new Map();
            sessions.forEach((session: any) => {
                sessionMap.set(session.sessionId || session.id, session);
            });

            dataToExport = await Promise.all(filteredSessionReviews.map(async (review: SessionReview) => {
                const session: SessionListModel = sessionMap.get(review.sessionId) || {};
                const applicationResult = await appContainer.loadCurrentAppById(review.applicationId);
                const applicationName = applicationResult?.data?.name || "Unknown App";
                const sessionLink = `${getBaseUrl()}/v/apps/${review.applicationId}/analytics/sessions?sessionId=${review.sessionId}`

                return {
                    "App Name": applicationName,
                    "Session Start Date": session.startDate || "",
                    "Session Summary": review.summary || "",
                    "Session Tags": Array.isArray(review.tags) ? review.tags.join(", ") : (review.tags || ""),
                    "Session Link": sessionLink,
                };
            }));
        } else {
            dataToExport = [];
        }

        return dataToExport;
    };

    const exportReport = async () => {
        setIsExporting(true);
        try {
            const rows = await getReportRows();
            if (!Array.isArray(rows)) {
                console.warn('No valid data to export');
                return;
            }

            // Extract unique tags from filtered session reviews
            const uniqueTags = [...new Set(
                filteredSessionReviews
                    .flatMap(review => Array.isArray(review.tags) ? review.tags : [])
                    .filter(tag => tag && tag.trim().length > 0)
            )];

            // Create filename tags from selected and excluded filter tags
            const filenameTagParts: string[] = [];

            // Add selected tags (include filters)
            selectedFilterTags.forEach(tag => {
                filenameTagParts.push(tag);
            });

            // Add excluded tags with NOT prefix
            excludedFilterTags.forEach(tag => {
                filenameTagParts.push(`NOT-${tag}`);
            });

            // Use filter tags for filename if available, otherwise fall back to unique tags
            const tagsForFilename = filenameTagParts.length > 0 ? filenameTagParts : uniqueTags;

            const now = new Date();
            const currentDate = now.toISOString().slice(0, 10);
            const currentTime = now.toISOString().slice(11, 19).replace(/:/g, '-'); // HH-MM-SS format
            const dateTimeString = `${currentDate}_${currentTime}`;

            let filename;
            if (filteredSessionReviews.length > 0 && tagsForFilename.length > 0) {
                // Include filter tags in filename: tags-date-time format
                const tagsString = tagsForFilename.join('-').replace(/[^a-zA-Z0-9-]/g, '_');
                filename = `${tagsString}-${dateTimeString}`;
            } else if (filteredSessionReviews.length > 0) {
                filename = `cross-org-filtered-report-${dateTimeString}`;
            } else {
                filename = `cross-org-report-${dateTimeString}`;
            }

            exportCSV(rows.length > 0 ? rows : [], columnHeaders, filename);
        } catch (error) {
            console.error('Error exporting report:', error);
        } finally {
            setTimeout(() => setIsExporting(false), 1000);
        }
    };

    useEffect(() => {
        const storageKey = currentOrgMode ? CURRENT_ORG_STORAGE_KEY : STORAGE_KEY;
        const stored = localStorage.getItem(storageKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                setOrgEntries(Array.isArray(parsed) ? parsed : []);
            } catch (error) {
                console.error('Error parsing stored org entries:', error);
                setOrgEntries([]);
            }
        } else {
            setOrgEntries([]);
        }
    }, [currentOrgMode]);

    useEffect(() => {
        const storageKey = currentOrgMode ? CURRENT_ORG_STORAGE_KEY : STORAGE_KEY;
        localStorage.setItem(storageKey, JSON.stringify(orgEntries));
    }, [orgEntries, currentOrgMode]);

    useEffect(() => {
        localStorage.setItem(DATE_FILTER_STORAGE_KEY, JSON.stringify(dateFilter));
    }, [dateFilter]);

    // Sync local date states when dateFilter changes from external sources (e.g., loading from storage)
    useEffect(() => {
        setLocalStartDate(dateFilter.startDate);
        setLocalEndDate(dateFilter.endDate);
    }, [dateFilter.startDate, dateFilter.endDate]);

    const availableOrgs = useMemo(() => {
        const usedIds = new Set(orgEntries.map(e => e.id));
        if (currentOrgMode) {
            // Show applications from current organization
            return currentOrgApplications
                .filter(app => app.id && app.name && !usedIds.has(app.id))
                .map(app => ({ value: app.id, label: app.name }));
        } else {
            // Show organizations
            return organizationContainer.state.organizations
                .filter(org => org.id && org.name && !usedIds.has(org.id))
                .map(org => ({ value: org.id, label: org.name }));
        }
    }, [currentOrgMode, organizationContainer.state.organizations, currentOrgApplications, orgEntries]);

    // Filter availableOrgs based on search term with advanced syntax
    const filteredAvailableOrgs = useMemo(() => {
        return availableOrgs.filter(item => {
            const itemName = item.label?.toLowerCase() || '';

            // Split by pipe to get individual search terms
            const terms = searchTerm.split('|').map(t => t.trim()).filter(t => t);

            // Separate exclusion terms and inclusion terms
            const exclusionTerms: string[] = [];
            const inclusionTerms: string[] = [];

            terms.forEach(term => {
                // Check if it's an exclusion term: !"text" or !'text'
                const exclusionMatch = term.match(/^!"(.+)"$/) || term.match(/^!'(.+)'$/);
                if (exclusionMatch) {
                    exclusionTerms.push(exclusionMatch[1].toLowerCase());
                } else {
                    // Remove quotes if present for regular terms
                    const cleanTerm = term.replace(/^["']|["']$/g, '');
                    inclusionTerms.push(cleanTerm.toLowerCase());
                }
            });

            exclusionTerms.push("manage menu");
            exclusionTerms.push("shared topics");
            // First check exclusions - if any exclusion term is found, exclude this item
            for (const excludeTerm of exclusionTerms) {
                if (itemName.includes(excludeTerm)) {
                    return false;
                }
            }

            // If no inclusion terms, show all (that aren't excluded)
            if (inclusionTerms.length === 0) {
                return true;
            }

            // Check if ALL inclusion terms match (AND logic)
            return inclusionTerms.every(term => itemName.includes(term));
        });
    }, [availableOrgs, searchTerm]);

    const handleAddOrgFromDropdown = (selectedOption: { value: string; label: string } | null) => {
        if (!selectedOption || !selectedOption.value) return;
        setOrgEntries(prev => [...prev, { id: selectedOption.value, name: selectedOption.label }]);
    };

    const selectFilteredItems = useCallback(() => {
        const newEntries = filteredAvailableOrgs.map(item => ({
            id: item.value,
            name: item.label
        }));
        setOrgEntries(prev => {
            const existingIds = new Set(prev.map(e => e.id));
            const uniqueNewEntries = newEntries.filter(entry => !existingIds.has(entry.id));
            return [...prev, ...uniqueNewEntries];
        });
    }, [filteredAvailableOrgs]);

    const updateOrgEntry = useCallback((index: number, field: keyof OrgEntry, value: string) => {
        setOrgEntries(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }, []);

    const removeOrgEntry = useCallback((index: number) => {
        setOrgEntries(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleStartDateChange = useCallback((value: string) => {
        // Always update local state to allow typing
        setLocalStartDate(value);
    }, []);

    const handleEndDateChange = useCallback((value: string) => {
        // Always update local state to allow typing
        setLocalEndDate(value);
    }, []);

    const handleStartDateBlur = useCallback(() => {
        // Only update the actual filter if the value is empty or valid when user leaves the field
        if (localStartDate === '' || (localStartDate.length >= 16 && !isNaN(Date.parse(localStartDate)))) {
            setDateFilter(prev => ({
                ...prev,
                startDate: localStartDate
            }));
            setIsDataLoading(true);
        } else {
            // Reset to last valid value if invalid
            setLocalStartDate(dateFilter.startDate);
        }
    }, [localStartDate, dateFilter.startDate]);

    const handleEndDateBlur = useCallback(() => {
        // Only update the actual filter if the value is empty or valid when user leaves the field
        if (localEndDate === '' || (localEndDate.length >= 16 && !isNaN(Date.parse(localEndDate)))) {
            setDateFilter(prev => ({
                ...prev,
                endDate: localEndDate
            }));
            setIsDataLoading(true);
        } else {
            // Reset to last valid value if invalid
            setLocalEndDate(dateFilter.endDate);
        }
    }, [localEndDate, dateFilter.endDate]);

    // Get organization IDs for cross-org mode - debounced to avoid frequent recalculations
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedOrgEntries(orgEntries);
            if (orgEntries.length > 0) {
                setIsDataLoading(true);
            }
        }, 5000); // 500ms debounce

        return () => clearTimeout(timer);
    }, [orgEntries]);

    const validOrgIds = useMemo((): string[] => {
        return debouncedOrgEntries
            .filter(entry => entry.id && entry.id.trim().length > 0)
            .map(entry => entry.id.trim());
    }, [debouncedOrgEntries]);

    // Memoize the processing change callback
    const handleProcessingChange = useCallback((isProcessing: boolean) => {
        setIsDataLoading(isProcessing);
    }, []);

    // Callback to receive filtered data from GenerativeReportTable
    const handleFilteredDataChange = useCallback((filteredReviews: SessionReview[], selectedTags?: string[], excludedTags?: string[]) => {
        const reviewsWithActionAttemptedOrOther = filteredReviews.filter(review =>
            review.category === 'ACTION_ATTEMPTED' || review.category === 'OTHER'
        );
        setFilteredSessionReviews(reviewsWithActionAttemptedOrOther);
        setSelectedFilterTags(selectedTags || []);
        setExcludedFilterTags(excludedTags || []);
    }, []);

    // Memoize the filter object to prevent unnecessary re-renders
    // Use timezone-aware dates so the server interprets them in the user's local timezone
    const memoizedFilter = useMemo((): AnalyticsRequestFilter =>
        buildTimezoneAwareDateFilter(dateFilter.startDate, dateFilter.endDate),
    [dateFilter.startDate, dateFilter.endDate]);

    return (
        <Container>
            <Header>
                <h1>{currentOrgMode ? 'Organization Analytics' : 'Cross Organization Session Review'}</h1>
                <ToggleButton onClick={() => setShowOrgManager(!showOrgManager)}>
                    {showOrgManager ? 'Hide Settings' : 'Settings'}
                </ToggleButton>
            </Header>

            {showOrgManager && (
                <SettingsSection>
                    <SectionTitle>{currentOrgMode ? 'Application Selection' : 'Organization Management'}</SectionTitle>
                    
                    <SearchBarContainer>
                        <SearchRow>
                            <SearchInput
                                type="text"
                                placeholder={`Search ${currentOrgMode ? 'applications' : 'organizations'} (e.g., item1|item2|!"exclude")`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <ResetButton onClick={() => setOrgEntries([])} disabled={orgEntries.length === 0}>
                                Reset Selected ({orgEntries.length})
                            </ResetButton>
                        </SearchRow>
                        <SearchInfo>
                            Use <code>|</code> to separate terms (AND logic). Use <code>!"text"</code> to exclude results containing "text". 
                            {filteredAvailableOrgs.length !== availableOrgs.length && (
                                <> Showing {filteredAvailableOrgs.length} of {availableOrgs.length} available.</>
                            )}
                        </SearchInfo>
                        <SearchRow>
                            <DropDownSelectField
                                options={filteredAvailableOrgs}
                                placeholder={filteredAvailableOrgs.length ? (currentOrgMode ? "Select application to add..." : "Select organization to add...") : (currentOrgMode ? "No more applications" : "No more organizations")}
                                value={null}
                                onChange={handleAddOrgFromDropdown}
                                searchable
                                clearable={false}
                                size="medium"
                            />
                            {filteredAvailableOrgs.length > 0 && (
                                <SelectFilteredButton onClick={selectFilteredItems}>
                                    Add Filtered {currentOrgMode ? 'Applications' : 'Organizations'} ({filteredAvailableOrgs.length})
                                </SelectFilteredButton>
                            )}
                        </SearchRow>
                    </SearchBarContainer>

                    <OrgList>
                        {orgEntries.map((entry, index) => (
                            <OrgEntryRow key={`org-${index}`}>
                                <InputGroup>
                                    <Label>{currentOrgMode ? 'Application ID:' : 'Organization ID:'}</Label>
                                    <Input
                                        type="text"
                                        placeholder={currentOrgMode ? "Enter application ID" : "Enter organization ID"}
                                        value={entry.id}
                                        onChange={(e) => updateOrgEntry(index, 'id', e.target.value)}
                                        disabled
                                    />
                                </InputGroup>
                                <InputGroup>
                                    <Label>{currentOrgMode ? 'Application Name:' : 'Organization Name:'}</Label>
                                    <Input
                                        type="text"
                                        placeholder={currentOrgMode ? "Enter application name" : "Enter organization name"}
                                        value={entry.name}
                                        onChange={(e) => updateOrgEntry(index, 'name', e.target.value)}
                                        disabled
                                    />
                                </InputGroup>
                                <RemoveButton onClick={() => removeOrgEntry(index)}>
                                    Remove
                                </RemoveButton>
                            </OrgEntryRow>
                        ))}
                    </OrgList>

                    <DateFilterSection>
                        <SectionTitle>Date Range Filter</SectionTitle>
                        <DateFilterRow>
                            <InputGroup>
                                <Label>Start Date:</Label>
                                <DateTimeInput
                                    type="datetime-local"
                                    value={localStartDate}
                                    onChange={(e) => handleStartDateChange(e.target.value)}
                                    onBlur={handleStartDateBlur}
                                />
                            </InputGroup>
                            <InputGroup>
                                <Label>End Date:</Label>
                                <DateTimeInput
                                    type="datetime-local"
                                    value={localEndDate}
                                    onChange={(e) => handleEndDateChange(e.target.value)}
                                    onBlur={handleEndDateBlur}
                                />
                            </InputGroup>
                        </DateFilterRow>
                        <ExportButtonRow>
                            <ExportReportButton
                                disabled={isExporting || isDataLoading || validOrgIds.length === 0}
                                onClick={exportReport}
                            >
                                {isExporting ? <Loader /> : null}
                                <span style={{ marginLeft: isExporting ? 8 : 0 }}>
                                    {filteredSessionReviews.length > 0
                                        ? `Export filtered report (${filteredSessionReviews.length} sessions)`
                                        : 'Export report'
                                    }
                                </span>
                                <img src={exportIcon} alt="Export" className="right-img" />
                            </ExportReportButton>
                        </ExportButtonRow>
                    </DateFilterSection>
                </SettingsSection>
            )}
            {dateFilter?.startDate?.length ?
                <ReportSection>
                    {React.useMemo(() => (
                        <GenerativeReportTable
                            applicationId=""
                            filter={memoizedFilter}
                            application={null}
                            sessionContainer={sessionContainer}
                            onProcessingChange={handleProcessingChange}
                            onFilteredDataChange={handleFilteredDataChange}
                            crossOrgMode={!currentOrgMode}
                            currentOrgMode={currentOrgMode}
                            orgIds={currentOrgMode ? undefined : validOrgIds}
                            appIds={currentOrgMode ? validOrgIds : undefined}
                            applications={currentOrgMode ? currentOrgApplications : undefined}
                            setUniqueEngagementCount={() => { }}
                            setConvertedCustomers={() => { }}
                            setUpsellPercentage={() => { }}
                            setRepeatCustomers={() => { }}
                        />
                    ), [memoizedFilter, validOrgIds, currentOrgMode, currentOrgApplications, sessionContainer, handleProcessingChange, handleFilteredDataChange])}
                </ReportSection>
                :
                <Loader />
            }
        </Container>
    );
};

const DropdownRow = styled.div`
    margin-bottom: 16px;
    max-width: 400px;
    min-width: 300px;
`;

const ExportReportButton = styled.button`
    margin: 0 0 16px 8px;
    font-size: 14px;
    font-weight: 400;
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-radius: 6px;
    border: none;
    background: #f5f7fa;
    color: #324661;
    cursor: pointer;
    .right-img {
        margin-left: 8px;
        width: 16px;
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ExportButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
`;
const Container = styled.div`
    padding: 24px;
    max-width: 1200px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    
    h1 {
        margin: 0;
        color: ${color_text_default};
    }
`;

const ToggleButton = styled.button`
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    
    &:hover {
        opacity: 0.9;
    }
`;

const SettingsSection = styled.div`
    background: ${color_shades_lightest};
    border: 1px solid ${color_shades_light};
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
    margin: 0 0 16px 0;
    color: ${color_text_default};
    font-size: 16px;
`;

const OrgList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
`;

const OrgEntryRow = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-end;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 6px;
    
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
`;

const InputGroup = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Label = styled.label`
    font-size: 12px;
    font-weight: 500;
    color: ${color_text_light};
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const Input = styled.input`
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px ${color_variants_ocean_light_1};
    }
`;

const RemoveButton = styled.button`
    background: ${color_colors_decline};
    color: ${color_defaults_white};
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    height: fit-content;
    
    &:hover {
        opacity: 0.9;
    }
`;

const AddButton = styled.button`
    background: ${color_colors_growth};
    color: ${color_defaults_white};
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    
    &:hover {
        opacity: 0.9;
    }
`;

const ReportSection = styled.div`
    margin-top: 24px;
`;

const DateFilterSection = styled.div`
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid ${color_shades_dark};
`;

const DateFilterRow = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-end;
    
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
`;

const DateTimeInput = styled.input`
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px ${color_variants_ocean_light_1};
    }
`;

const SearchBarContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    margin-bottom: 16px;
`;

const SearchRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const SearchInput = styled.input`
    flex: 1;
    padding: 12px 16px;
    font-size: 14px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    background: ${color_defaults_white};
    color: ${color_text_default};

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 2px ${color_variants_ocean_light_1};
    }

    &::placeholder {
        color: ${color_text_light};
    }
`;

const ResetButton = styled.button`
    padding: 10px 16px;
    background: ${color_colors_decline};
    color: ${color_defaults_white};
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        opacity: 0.9;
        transform: translateY(-1px);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SearchInfo = styled.div`
    font-size: 13px;
    color: ${color_text_light};

    code {
        background: ${color_shades_lightest};
        padding: 2px 6px;
        border-radius: 3px;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
        font-size: 12px;
        color: ${color_colors_ocean};
    }
`;

const SelectFilteredButton = styled.button`
    padding: 10px 16px;
    background: ${color_colors_growth};
    color: ${color_defaults_white};
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
`;

export default CrossOrgSessionReview;
